/**
 * What to call a customer on screen.
 *
 * Only the fields this needs, so any shape the API returns satisfies it.
 */
export interface NameableCustomer {
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  phone?: string | null;
  email?: string | null;
}

/** Shown when a customer has no identifying detail worth printing. */
export const UNNAMED_CUSTOMER = 'Unnamed customer';

/**
 * A label for a customer, never an empty string.
 *
 * Two copies of this used to live in CustomersPage and CustomerDetailPage, both
 * ending `return c.companyName ?? ''`. That was survivable only while the
 * server refused to store a customer with no name at all. Names are optional
 * now (operator decision, 2026-07-31: real customers turn up identified by a
 * phone number and nothing else), so the old version would render a blank row
 * — present, clickable, and impossible to tell apart from its neighbours.
 *
 * The fallback order is how someone would actually identify them: what you call
 * them, then who they are, then how you reach them.
 */
export function customerLabel(customer: NameableCustomer): string {
  const person = [customer.firstName, customer.lastName]
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .join(' ')
    .trim();
  if (person) return person;

  const company = customer.companyName?.trim();
  if (company) return company;

  const email = customer.email?.trim();
  if (email) return email;

  const phone = customer.phone?.trim();
  if (phone) return phone;

  return UNNAMED_CUSTOMER;
}
